import { useState } from "react"
import {
  Music,
  Play,
  Pause,
  Loader2,
  AlertCircle,
  CheckCircle,
  MoreVertical,
  Trash2,
  RefreshCw,
  Edit3,
} from "lucide-react"

import { Button, Card, CardContent } from "@aix/ui"
import { trpc } from "../../../lib/trpc"

import { CamelotKeySelector } from "./CamelotKeySelector"

// Status badge colors
const STATUS_CONFIG = {
  uploading: { label: "Uploading", color: "bg-yellow-500", icon: Loader2 },
  uploaded: { label: "Processing", color: "bg-blue-500", icon: Loader2 },
  separating: { label: "Separating", color: "bg-purple-500", icon: Loader2 },
  analyzing: { label: "Analyzing", color: "bg-indigo-500", icon: Loader2 },
  ready: { label: "Ready", color: "bg-green-500", icon: CheckCircle },
  error: { label: "Error", color: "bg-red-500", icon: AlertCircle },
} as const

interface Song {
  id: string
  title: string
  artist: string | null
  genre: string
  status: keyof typeof STATUS_CONFIG
  bpm: number | null
  camelotKey: string | null
  duration: number | null
  originalFileUrl: string | null
  vocalsFileUrl: string | null
  instrumentalFileUrl: string | null
  errorMessage: string | null
  createdAt: Date
}

interface SongCardProps {
  song: Song
  onRefresh: () => void
}

export function SongCard({ song, onRefresh }: SongCardProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [playingTrack, setPlayingTrack] = useState<"original" | "vocals" | "instrumental" | null>(null)
  const [showMenu, setShowMenu] = useState(false)
  const [isEditingKey, setIsEditingKey] = useState(false)

  const deleteMutation = trpc.mashupSong.delete.useMutation({
    onSuccess: () => onRefresh(),
  })

  const retryMutation = trpc.mashupSong.retryProcessing.useMutation({
    onSuccess: () => onRefresh(),
  })

  const updateMutation = trpc.mashupSong.update.useMutation({
    onSuccess: () => {
      onRefresh()
      setIsEditingKey(false)
    },
  })

  const statusConfig = STATUS_CONFIG[song.status]
  const StatusIcon = statusConfig.icon

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "--:--"
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  const handlePlay = (track: "original" | "vocals" | "instrumental") => {
    const url =
      track === "original" ? song.originalFileUrl : track === "vocals" ? song.vocalsFileUrl : song.instrumentalFileUrl

    if (!url) return

    // In a real implementation, you'd use a proper audio player
    // For now, just toggle state
    if (playingTrack === track && isPlaying) {
      setIsPlaying(false)
      setPlayingTrack(null)
    } else {
      setIsPlaying(true)
      setPlayingTrack(track)
    }
  }

  const handleDelete = () => {
    if (confirm("Delete this song? This cannot be undone.")) {
      deleteMutation.mutate({ id: song.id })
    }
    setShowMenu(false)
  }

  const handleRetry = () => {
    retryMutation.mutate({ songId: song.id })
    setShowMenu(false)
  }

  return (
    <Card className="group hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Music className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="font-medium truncate">{song.title}</h3>
              {song.artist && <p className="text-sm text-muted-foreground truncate">{song.artist}</p>}
            </div>
          </div>

          {/* Menu */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100"
              onClick={() => setShowMenu(!showMenu)}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 bg-popover border border-border rounded-md shadow-lg z-10 min-w-32">
                {song.status === "error" && (
                  <button
                    className="w-full px-3 py-2 text-sm text-left hover:bg-accent flex items-center gap-2"
                    onClick={handleRetry}
                    disabled={retryMutation.isLoading}
                  >
                    <RefreshCw className="h-4 w-4" />
                    Retry Processing
                  </button>
                )}
                <button
                  className="w-full px-3 py-2 text-sm text-left hover:bg-accent text-destructive flex items-center gap-2"
                  onClick={handleDelete}
                  disabled={deleteMutation.isLoading}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Status Badge */}
        <div className="flex items-center gap-2 mb-3">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-white rounded-full ${statusConfig.color}`}
          >
            <StatusIcon
              className={`h-3 w-3 ${
                ["uploading", "uploaded", "separating", "analyzing"].includes(song.status) ? "animate-spin" : ""
              }`}
            />
            {statusConfig.label}
          </span>
          <span className="px-2 py-0.5 text-xs bg-secondary text-secondary-foreground rounded-full capitalize">
            {song.genre.replace("_", " ")}
          </span>
        </div>

        {/* Error Message */}
        {song.status === "error" && song.errorMessage && (
          <div className="mb-3 p-2 bg-destructive/10 border border-destructive/20 rounded text-xs text-destructive">
            {song.errorMessage}
          </div>
        )}

        {/* Metadata */}
        <div className="grid grid-cols-3 gap-2 text-sm mb-3">
          <div>
            <span className="text-muted-foreground">BPM</span>
            <p className="font-medium">{song.bpm?.toFixed(1) || "--"}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Key</span>
            {isEditingKey ? (
              <div className="mt-1">
                <CamelotKeySelector
                  value={song.camelotKey}
                  onChange={(key) => {
                    updateMutation.mutate({
                      id: song.id,
                      camelotKey: key,
                    })
                  }}
                  compact
                  disabled={updateMutation.isLoading}
                />
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <p className="font-medium">{song.camelotKey || "--"}</p>
                {song.status === "ready" && (
                  <button
                    onClick={() => setIsEditingKey(true)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-accent rounded"
                    title="Edit key"
                  >
                    <Edit3 className="h-3 w-3 text-muted-foreground" />
                  </button>
                )}
              </div>
            )}
          </div>
          <div>
            <span className="text-muted-foreground">Duration</span>
            <p className="font-medium">{formatDuration(song.duration)}</p>
          </div>
        </div>

        {/* Play Controls (only for ready songs) */}
        {song.status === "ready" && (
          <div className="flex gap-1">
            <Button
              variant={playingTrack === "original" ? "default" : "outline"}
              size="sm"
              className="flex-1 text-xs"
              onClick={() => handlePlay("original")}
              disabled={!song.originalFileUrl}
            >
              {playingTrack === "original" && isPlaying ? (
                <Pause className="h-3 w-3 mr-1" />
              ) : (
                <Play className="h-3 w-3 mr-1" />
              )}
              Original
            </Button>
            <Button
              variant={playingTrack === "vocals" ? "default" : "outline"}
              size="sm"
              className="flex-1 text-xs"
              onClick={() => handlePlay("vocals")}
              disabled={!song.vocalsFileUrl}
            >
              {playingTrack === "vocals" && isPlaying ? (
                <Pause className="h-3 w-3 mr-1" />
              ) : (
                <Play className="h-3 w-3 mr-1" />
              )}
              Vocals
            </Button>
            <Button
              variant={playingTrack === "instrumental" ? "default" : "outline"}
              size="sm"
              className="flex-1 text-xs"
              onClick={() => handlePlay("instrumental")}
              disabled={!song.instrumentalFileUrl}
            >
              {playingTrack === "instrumental" && isPlaying ? (
                <Pause className="h-3 w-3 mr-1" />
              ) : (
                <Play className="h-3 w-3 mr-1" />
              )}
              Inst.
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
